import {
  inboundShipmentFileIdFormat,
  inboundShipmentIdFormat,
  inboundShipmentProductIdFormat,
  locationIdFormat,
  productFamilyIdFormat,
  productIdFormat,
  purchaseOrderIdFormat,
  supplierIdFormat,
} from "@redotech/redo-model/ids";
import * as zExt from "@redotech/rpc/ext";
import * as z from "zod/v4";
import { InboundShipmentStatus } from "./get-shipments";

const ShipmentProductSchema_ = z.object({
  id: inboundShipmentProductIdFormat.schema,
  productId: productIdFormat.schema,
  purchaseOrderId: purchaseOrderIdFormat.schema,
  title: z.string(),
  sku: z.string().nullish(),
  upc: z.string().nullish(),
  barcodes: z.array(z.string()),
  image: z.string().nullish(),
  productFamilyId: productFamilyIdFormat.schema.nullish(),
  productFamilyTitle: z.string().nullish(),
  quantity: z.number().int(),
  receivedQuantity: z.number().int(),
  discrepancy: z.number().int(),
  damagedQuantity: z.number().int(),
});

export const ShipmentProductSchema: z.ZodType<ShipmentProduct> =
  ShipmentProductSchema_;
export interface ShipmentProduct
  extends z.infer<typeof ShipmentProductSchema_> {}

// Inbound shipment file
export const InboundShipmentFileSchema_ = z.object({
  id: inboundShipmentFileIdFormat.schema,
  title: z.string(),
  url: z.string(),
  contentType: z.string(),
  createdBy: z.object({ name: z.string(), email: z.string() }),
  createdAt: zExt.instant(),
});

export const InboundShipmentFileSchema: z.ZodType<InboundShipmentFile> =
  InboundShipmentFileSchema_;
export interface InboundShipmentFile
  extends z.infer<typeof InboundShipmentFileSchema_> {}

const ShipmentDetailsSchema_ = z.object({
  id: inboundShipmentIdFormat.schema,
  shipmentId: z.string().nullish(),
  purchaseOrders: z.array(
    z.object({ id: purchaseOrderIdFormat.schema, poNumber: z.string() }),
  ),
  status: z.enum([
    InboundShipmentStatus.PENDING,
    InboundShipmentStatus.IN_TRANSIT,
    InboundShipmentStatus.PARTIALLY_RECEIVED,
    InboundShipmentStatus.RECEIVED,
  ]),
  location: z
    .object({ id: locationIdFormat.schema, name: z.string() })
    .nullish(),
  supplier: z
    .object({ id: supplierIdFormat.schema, name: z.string() })
    .nullish(),
  carrier: z.string().nullish(),
  trackingNumber: z.string().nullish(),
  trackingUrl: z.string().nullish(),
  estimatedArrivalDate: zExt.plainDate().nullish(),
  freightType: z.string().nullish(),
  paymentTerms: z.string().nullish(),
  freightCost: z.number(),
  freightCurrency: z.string(),
  dutyCost: z.number(),
  dutyCurrency: z.string(),
  products: z.array(ShipmentProductSchema),
  files: z.array(InboundShipmentFileSchema),
  skuCount: z.number().int(),
  itemCount: z.number().int(),
  receivedCount: z.number().int(),
  damagedCount: z.number().int(),
  createdAt: zExt.instant(),
  updatedAt: zExt.instant(),
});

export const ShipmentDetailsSchema: z.ZodType<ShipmentDetails> =
  ShipmentDetailsSchema_;
export interface ShipmentDetails
  extends z.infer<typeof ShipmentDetailsSchema_> {}

export const getShipment = {
  name: "getShipment" as const,
  input: z.object({ shipmentId: inboundShipmentIdFormat.schema }),
  output: z.object({ shipment: ShipmentDetailsSchema }),
};
